import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Input from '../../components/Fields/Input';
import { Field } from '../../components/Fields/Field';
import KYCButton from '../Onboarding/KYCButton';
import { queryClient } from '../../services/NetworkManager';
import useAgreement, {
  AGREEMENT_QUERY_KEY,
  AgreementDetails,
  AgreementOverview,
  EntityType,
  RegistrationType,
  RelationType,
  getSignedAgreementUrl,
  startAgreementEsign,
  syncAgreementEsign,
} from './useAgreement';

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'LLP', label: 'LLP' },
  { value: 'HUF', label: 'HUF' },
  { value: 'PROPRIETORSHIP', label: 'Sole proprietorship' },
];

const RELATION_TYPES: { value: RelationType; label: string }[] = [
  { value: 'SON_OF', label: 'Son of (s/o)' },
  { value: 'DAUGHTER_OF', label: 'Daughter of (d/o)' },
  { value: 'WIFE_OF', label: 'Wife of (w/o)' },
];

const REGISTRATION_TYPES: { value: RegistrationType; label: string }[] = [
  { value: 'CIN', label: 'CIN' },
  { value: 'LLPIN', label: 'LLPIN' },
  { value: 'GSTIN', label: 'GSTIN' },
  { value: 'OTHER', label: 'Other' },
];

const formatDate = (value?: string) => (value ? dayjs(value).format('D MMM YYYY') : '-');

const errorMessage = (error: any) =>
  error?.response?.data?.message || 'Something went wrong. Please try again.';

const isIndividual = (type?: string) => type === 'INDIVIDUAL';
const requiredWhen = (individual: boolean, message: string) => (s: yup.StringSchema) =>
  s.when('entityType', {
    is: (type: string) => isIndividual(type) === individual,
    then: (r) => r.trim().required(message),
    otherwise: (r) => r.nullable(),
  });

// Which fields are mandatory follows the agreement's blanks. Formats (PAN, CIN, LLPIN, GSTIN,
// email) are checked by the backend, the single owner of those rules.
const schema = yup.object().shape({
  entityType: yup.string().required('Please select an option'),
  age: yup
    .number()
    .transform((value, original) => (original === '' ? null : value))
    .nullable()
    .when('entityType', {
      is: isIndividual,
      then: (s) =>
        s.required('Age is required').min(18, 'Age must be 18 or above').max(120, 'Enter a valid age'),
    }),
  relationType: requiredWhen(true, 'Please select a relation')(yup.string()),
  relationName: requiredWhen(true, "Father's / husband's name is required")(yup.string()),
  entityName: requiredWhen(false, 'Name is required')(yup.string()),
  registrationType: requiredWhen(false, 'Please select a registration type')(yup.string()),
  registrationNumber: requiredWhen(false, 'Registration number is required')(yup.string()),
  entityPan: requiredWhen(false, 'PAN is required')(yup.string()),
  registeredOffice: requiredWhen(false, 'Registered office is required')(yup.string()),
  noticeAddress: yup.string().trim().required('Address is required'),
  noticeAttention: yup.string().trim().required('Attention is required'),
  noticeEmail: yup.string().trim().required('Email is required'),
});

const defaultsFrom = (overview: AgreementOverview): AgreementDetails => {
  const last = overview.latest?.details;
  const prefill = overview.prefill || {};
  const registrationFromKyc: RegistrationType | null = prefill.cin
    ? 'CIN'
    : prefill.gstin
      ? 'GSTIN'
      : null;
  return {
    entityType: last?.entityType || 'INDIVIDUAL',
    age: last?.age ?? prefill.age ?? null,
    relationType: last?.relationType || null,
    relationName: last?.relationName || '',
    entityName: last?.entityName || '',
    registrationType: last?.registrationType || registrationFromKyc,
    registrationNumber:
      last?.registrationNumber ||
      (registrationFromKyc === 'CIN' ? prefill.cin : prefill.gstin) ||
      '',
    entityPan: last?.entityPan || '',
    registeredOffice: last?.registeredOffice || '',
    noticeAddress: last?.noticeAddress || prefill.address || '',
    noticeAttention: last?.noticeAttention || prefill.name || '',
    noticeEmail: last?.noticeEmail || prefill.email || '',
  };
};

const ReadOnlyRow: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:gap-4 text-sm py-2 border-b border-stone-100 last:border-0">
    <span className="sm:w-32 shrink-0 text-stone-500">{label}</span>
    <span className="text-stone-800 font-medium break-words">{value || '-'}</span>
  </div>
);

const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & {
    options: { value: string; label: string }[];
    placeholder?: string;
  }
>(({ options, placeholder, ...rest }, ref) => (
  <div className="relative border flex border-gray-300 rounded-lg overflow-hidden shadow-sm">
    <select ref={ref} className="w-full px-3 py-2.5 text-sm bg-white outline-none" {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
));
Select.displayName = 'Select';

const Agreement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { overview, isLoading } = useAgreement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const refreshAll = () =>
    Promise.all([
      queryClient.invalidateQueries(AGREEMENT_QUERY_KEY),
      // kycStatus on the partner profile drives the lock, banner and navigation.
      queryClient.invalidateQueries('getPartner'),
    ]);

  const checkSignature = async (esignId: string) => {
    setIsChecking(true);
    try {
      const response = await syncAgreementEsign(esignId);
      await refreshAll();
      if (response?.data?.latest?.status === 'SIGNED') {
        toast.success('Agreement signed successfully.');
      }
    } catch (error) {
      toast.error(errorMessage(error));
    }
    setIsChecking(false);
  };

  // Digio sends the partner back to /agreement?return=<esignId>; Digio itself is asked for the result.
  useEffect(() => {
    const returnedEsignId = searchParams.get('return');
    if (returnedEsignId) {
      navigate('/agreement', { replace: true });
      checkSignature(returnedEsignId);
    }
    // Once, on arrival from Digio; the query param is removed straight away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<AgreementDetails>({ resolver: yupResolver(schema) as any });

  useEffect(() => {
    if (overview) {
      reset(defaultsFrom(overview));
    }
  }, [overview, reset]);

  const entityType = watch('entityType');
  const individual = isIndividual(entityType);

  const onSubmit = async (details: AgreementDetails) => {
    setIsSubmitting(true);
    try {
      // Send only the fields that belong to the chosen entity type.
      const response = await startAgreementEsign({
        entityType: details.entityType,
        ...(individual
          ? {
              age: details.age,
              relationType: details.relationType,
              relationName: details.relationName,
            }
          : {
              entityName: details.entityName,
              registrationType: details.registrationType,
              registrationNumber: details.registrationNumber,
              entityPan: details.entityPan,
            }),
        registeredOffice: details.registeredOffice || null,
        noticeAddress: details.noticeAddress,
        noticeAttention: details.noticeAttention,
        noticeEmail: details.noticeEmail,
      });
      window.location.assign(response.data.signingUrl);
    } catch (error) {
      toast.error(errorMessage(error));
      setIsSubmitting(false);
    }
  };

  const download = async (esignId: string) => {
    try {
      const response = await getSignedAgreementUrl(esignId);
      window.open(response.data.url, '_blank');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  if (isLoading || !overview) {
    return <div className="my-6 max-w-3xl mx-auto text-sm text-stone-500">Loading…</div>;
  }

  const { status, latest, prefill } = overview;
  const canSign =
    overview.esignEnabled && ['PENDING', 'EXPIRING_SOON', 'RENEWAL_DUE', 'EXPIRED'].includes(status);
  const awaitingDigio = latest?.status === 'SIGN_PENDING';

  return (
    <div className="my-6 max-w-3xl mx-auto flex flex-col gap-6">
      <h1 className="text-xl lg:text-2xl font-semibold text-teal-950">Partner Agreement</h1>

      <div className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col gap-2">
        {(status === 'NOT_REQUIRED' || status === 'PREVIOUSLY_ACCEPTED') && (
          <p className="text-sm text-stone-700">
            No partner agreement is required for your account.
          </p>
        )}
        {status === 'PENDING' && (
          <p className="text-sm text-stone-700">
            Please fill in the details below and sign the agreement using Aadhaar e-sign.
          </p>
        )}
        {status === 'ACTIVE' && (
          <p className="text-sm text-stone-700">
            Your agreement is valid till <b>{formatDate(overview.validUntil)}</b>.
          </p>
        )}
        {status === 'EXPIRING_SOON' && (
          <p className="text-sm text-stone-700">
            Your agreement is valid till <b>{formatDate(overview.validUntil)}</b>. Please renew it
            before it expires.
          </p>
        )}
        {status === 'RENEWAL_DUE' && (
          <p className="text-sm text-stone-700">
            Your agreement expired on <b>{formatDate(overview.validUntil)}</b>. Please renew it by{' '}
            <b>{formatDate(overview.renewBy)}</b>.
          </p>
        )}
        {status === 'EXPIRED' && (
          <p className="text-sm text-stone-700">
            Your agreement expired on <b>{formatDate(overview.validUntil)}</b> and the renewal date
            has passed. Please renew it.
          </p>
        )}
        {awaitingDigio && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-sm text-stone-800">
              We are verifying your signature. This may take a few minutes.
            </p>
            <button
              type="button"
              disabled={isChecking}
              onClick={() => checkSignature(latest!.publicId)}
              className="shrink-0 h-9 rounded-lg px-4 text-sm font-semibold text-green-700 border border-green-700 disabled:opacity-60"
            >
              {isChecking ? 'Checking…' : 'Refresh'}
            </button>
          </div>
        )}
        {latest?.status === 'FAILED' && (
          <p className="text-sm text-red-700 mt-2">
            E-sign was not completed. Please try again.
          </p>
        )}
        {latest?.status === 'SIGNED' && (
          <button
            type="button"
            onClick={() => download(latest.publicId)}
            className="self-start mt-2 text-sm font-semibold text-green-700 underline underline-offset-2"
          >
            Download Agreement
          </button>
        )}
      </div>

      {canSign && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="rounded-xl border border-stone-200 bg-white p-5 flex flex-col gap-5"
        >
          <Field label="Signing As" error={errors.entityType?.message}>
            <Select options={ENTITY_TYPES} {...register('entityType')} />
          </Field>

          {individual ? (
            <>
              <div>
                <h2 className="text-base font-semibold text-stone-800">Your Details</h2>
                <p className="text-xs text-stone-500 mt-1">
                  As per your KYC records.
                </p>
                <div className="mt-3">
                  <ReadOnlyRow label="Name" value={prefill?.name} />
                  <ReadOnlyRow label="PAN" value={prefill?.pan} />
                </div>
              </div>
              <Field label="Age" error={errors.age?.message}>
                <Input type="text" numericOnly placeholder="Enter age" {...register('age')} />
              </Field>
              <Field label="Relation" error={errors.relationType?.message}>
                <Select
                  options={RELATION_TYPES}
                  placeholder="Select"
                  {...register('relationType')}
                />
              </Field>
              <Field label="Father's / Husband's Name" error={errors.relationName?.message}>
                <Input type="text" placeholder="Enter father's / husband's name" {...register('relationName')} />
              </Field>
              <Field label="Registered Office (optional)" error={errors.registeredOffice?.message}>
                <Input
                  type="text"
                  placeholder="Enter office address"
                  {...register('registeredOffice')}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Name" error={errors.entityName?.message}>
                <Input type="text" placeholder="Enter registered name" {...register('entityName')} />
              </Field>
              <Field label="Registration Type" error={errors.registrationType?.message}>
                <Select
                  options={REGISTRATION_TYPES}
                  placeholder="Select"
                  {...register('registrationType')}
                />
              </Field>
              <Field
                label="Registration Number"
                error={errors.registrationNumber?.message}
              >
                <Input
                  type="text"
                  placeholder="Enter registration number"
                  {...register('registrationNumber')}
                />
              </Field>
              <Field label="PAN" error={errors.entityPan?.message}>
                <Input
                  type="text"
                  placeholder="Enter PAN"
                  {...register('entityPan')}
                />
              </Field>
              <Field label="Registered Office" error={errors.registeredOffice?.message}>
                <Input
                  type="text"
                  placeholder="Enter registered office address"
                  {...register('registeredOffice')}
                />
              </Field>
            </>
          )}

          <div>
            <h2 className="text-base font-semibold text-stone-800">Notice Details</h2>
            <p className="text-xs text-stone-500 mt-1">
              Address for official communication related to this agreement.
            </p>
          </div>
          <Field label="Address" error={errors.noticeAddress?.message}>
            <Input type="text" placeholder="Enter address" {...register('noticeAddress')} />
          </Field>
          <Field label="Attention" error={errors.noticeAttention?.message}>
            <Input
              type="text"
              placeholder="Enter contact person name"
              {...register('noticeAttention')}
            />
          </Field>
          <Field label="Email" error={errors.noticeEmail?.message}>
            <Input type="text" placeholder="Enter email" {...register('noticeEmail')} />
          </Field>

          <KYCButton
            text={status === 'PENDING' ? 'Proceed to E-sign' : 'Proceed to Renew'}
            isLoading={isSubmitting}
            className="!mt-2"
          />
        </form>
      )}
    </div>
  );
};

Agreement.displayName = 'Agreement';

export default Agreement;
